import React, { useEffect, useState } from "react";
import i18n from "i18next";
import { Dispatch } from "redux";
import { useDispatch } from "react-redux";

// Fluent v9
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  Combobox,
  Text,
  Option,
} from "@fluentui/react-components";
import { StackShim } from "@fluentui/react-migration-v8-v9";

// Import function and constants
import { fetchWrapper } from "../../../helpers/fetchwrapper";
import { baseUrl } from "../../../helpers/constants";
import { setAppState } from "../../../store/actionCreators";

// Import Models
import { IAppStateDto } from "../../../Models/IAppStateDto";
import { IAddUserBedarfPosition } from "../../../Models/IAddUserBedarfPosition";
import {
  AddFilled,
  ArrowLeftRegular,
  ShoppingBagAddRegular,
  SubtractFilled,
} from "@fluentui/react-icons";
import { IArtikel } from "../../../Models/IArtikel";

interface IAddToBedarfDialogProps {
  onDismiss: any;
  addUserBedarfPosition?: IAddUserBedarfPosition | undefined;
  open?: boolean;
}

function AddToBedarfDialog(props: IAddToBedarfDialogProps) {
  const [addUserBedarfPosition, setAddUserBedarfPosition] = useState<
    IAddUserBedarfPosition | undefined
  >(undefined);

  const [artikelToAdd, setartikelToAdd] = useState<IArtikel[] | undefined>();

  const handleEditBedarfValuesChange = (name: string, val: any) => {
    setAddUserBedarfPosition((prevState: any) => ({
      ...prevState,
      [name]: val,
    }));
  };

  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.addUserBedarfPosition != undefined) {
      setAddUserBedarfPosition(props.addUserBedarfPosition);

      setError(undefined);
    }
  }, [props.addUserBedarfPosition]);

  const addToBedarfsliste = () => {
    if (addUserBedarfPosition) {
      fetchWrapper
        .post(`${baseUrl}/api/userbedarfe/add`, addUserBedarfPosition)
        .then((result: any) => {
          setAddUserBedarfPosition(undefined);
          refreshAppState();
          props.onDismiss();
        })
        .catch((error) => {
          setError(error);
        });
    }
  };

  const get10ArtikelToAdd = (artikelnr: string) => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/lsp3/ByNumber?artikelnr=${artikelnr}`)
      .then((artikel: IArtikel[]) => {
        console.log(artikel);
        setartikelToAdd(artikel);
      });
  };

  const dispatch: Dispatch<any> = useDispatch();

  const refreshAppState = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/account/GetAppState`)
      .then((data: IAppStateDto) => {
        dispatch(setAppState(data));
      });
  };

  return (
    <Dialog open={addUserBedarfPosition != undefined || props.open == true}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{i18n.t<string>("addToBedarf")}</DialogTitle>
          <DialogContent>
            {addUserBedarfPosition && (
              <StackShim tokens={{ childrenGap: 5 }}>
                <Text>{i18n.t<string>("addToBedarfText")}</Text>

                {addUserBedarfPosition.artikel && (
                  <Field label={i18n.t<string>("artikelId")}>
                    <Input
                      appearance="filled-darker"
                      readOnly
                      value={addUserBedarfPosition.artikel ?? ""}
                    />
                  </Field>
                )}

                {addUserBedarfPosition.produkt && (
                  <Field label={i18n.t<string>("product")}>
                    <Input
                      appearance="filled-darker"
                      readOnly
                      value={addUserBedarfPosition.produkt ?? ""}
                    />
                  </Field>
                )}

                <Field label={i18n.t<string>("benennung")}>
                  <Input
                    readOnly
                    appearance="filled-darker"
                    value={addUserBedarfPosition.benennung ?? ""}
                  />
                </Field>

                <Field label={i18n.t<string>("kundenReferenz")}>
                  <Input
                    value={addUserBedarfPosition?.kundenReferenz}
                    onChange={(ev, data) => {
                      handleEditBedarfValuesChange(
                        "kundenReferenz",
                        data.value,
                      );
                    }}
                  />
                </Field>

                <Field label={i18n.t<string>("menge")}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      appearance="primary"
                      shape="circular"
                      size="small"
                      icon={<SubtractFilled />}
                      onClick={() => {
                        if (
                          addUserBedarfPosition.menge &&
                          addUserBedarfPosition.menge >= 1
                        ) {
                          handleEditBedarfValuesChange(
                            "menge",
                            addUserBedarfPosition.menge - 1,
                          );
                        }
                      }}
                    />
                    <Input
                      style={{
                        width: "50px",
                        marginLeft: "5px",
                        marginRight: "5px",
                      }}
                      value={addUserBedarfPosition.menge?.toString()}
                      onChange={(ev, data) =>
                        handleEditBedarfValuesChange(
                          "menge",
                          Number(data.value),
                        )
                      }
                    />
                    <Button
                      appearance="primary"
                      shape="circular"
                      size="small"
                      icon={<AddFilled />}
                      onClick={() => {
                        if (addUserBedarfPosition.menge) {
                          handleEditBedarfValuesChange(
                            "menge",
                            addUserBedarfPosition.menge + 1,
                          );
                        }
                      }}
                    />
                  </div>
                </Field>

                {error && (
                  <div style={{ marginTop: 10 }}>
                    <MessageBar intent="error">{error}</MessageBar>
                  </div>
                )}
              </StackShim>
            )}
            {!addUserBedarfPosition && (
              <>
                <Field label={i18n.t<string>("enterValidArtikelId")}>
                  <Combobox
                    onOptionSelect={(ev, data) => {
                      var benennung = i18n.language.includes("de")
                        ? artikelToAdd?.find(
                            (x) => x.artikelId == data.optionValue,
                          )?.benennungDE
                        : artikelToAdd?.find(
                            (x) => x.artikelId == data.optionValue,
                          )?.benennungEN;
                      var addBedarf: IAddUserBedarfPosition = {
                        menge: 1,
                        artikel: data.optionValue,
                        produkt: undefined,
                        benennung: benennung,
                        reference: undefined,
                        kundenReferenz: undefined,
                      };
                      setAddUserBedarfPosition(addBedarf);
                    }}
                    onChange={(ev) => {
                      get10ArtikelToAdd(ev.currentTarget.value as string);
                    }}
                  >
                    {artikelToAdd != undefined &&
                      artikelToAdd.map((item) => {
                        var benennung = i18n.language.includes("de")
                          ? item.benennungDE
                          : item.benennungEN;
                        var optionString: string = `${item.artikelId}  -  ${benennung}`;

                        return (
                          <Option value={item.artikelId ?? ""}>
                            {optionString}
                          </Option>
                        );
                      })}
                  </Combobox>
                </Field>
              </>
            )}
          </DialogContent>
          <DialogActions position="start">
            {props.open == true && addUserBedarfPosition != undefined && (
              <Button
                icon={<ArrowLeftRegular />}
                onClick={() => {
                  setAddUserBedarfPosition(undefined);
                }}
              >
                {i18n.t<string>("back")}
              </Button>
            )}
          </DialogActions>

          <DialogActions position="end">
            <Button
              onClick={() => {
                props.onDismiss();
                setAddUserBedarfPosition(undefined);
              }}
            >
              {i18n.t("cancel")}
            </Button>

            {addUserBedarfPosition != undefined && (
              <Button
                appearance="primary"
                style={{ width: "230px" }}
                icon={<ShoppingBagAddRegular />}
                onClick={() => {
                  addToBedarfsliste();
                }}
              >
                {i18n.t("addToBedarf")}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default AddToBedarfDialog;
